import {
  Button,
  Calendar,
  CalendarProps,
  Checkbox,
  CloseIcon,
  ConfiguredCalendar,
  FilterIcon,
} from '@repo/atomic-components';
import { classNames } from 'primereact/utils';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface UploadDateRange {
  minUploadDate?: string;
  maxUploadDate?: string;
}

interface UploadedByHeaderFilterProps {
  columnId: string;
  filterOptions: string[];
  currentFilterValue: string[];
  currentDateRange: UploadDateRange;
  onApply: (values: string[], dateRange: UploadDateRange) => void;
  filterAnchor: string | null;
  setFilterAnchor: (columnId: string | null) => void;
}

type FilterTab = 'name' | 'date';

const parseDate = (value?: string): Date | null => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatDate = (date: Date | null | undefined): string | undefined => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const UploadedByHeaderFilter = ({
  columnId,
  filterOptions,
  currentFilterValue,
  currentDateRange,
  onApply,
  filterAnchor,
  setFilterAnchor,
}: UploadedByHeaderFilterProps) => {
  const [activeTab, setActiveTab] = useState<FilterTab>('name');
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingNames, setPendingNames] = useState<string[]>(currentFilterValue);
  const [pendingDateRange, setPendingDateRange] = useState<UploadDateRange>(currentDateRange);
  const [popoverPosition, setPopoverPosition] = useState<{ left: number; top: number } | null>(null);
  const iconRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const isOpen = filterAnchor === columnId;
  const isActive =
    currentFilterValue.length > 0 || Boolean(currentDateRange.minUploadDate || currentDateRange.maxUploadDate);

  const filteredOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return query ? filterOptions.filter((option) => option.toLowerCase().includes(query)) : filterOptions;
  }, [filterOptions, searchQuery]);

  const pendingDates = useMemo((): (Date | null)[] | null => {
    const minDate = parseDate(pendingDateRange.minUploadDate);
    const maxDate = parseDate(pendingDateRange.maxUploadDate);

    if (!minDate) return null;
    return [minDate, maxDate];
  }, [pendingDateRange.maxUploadDate, pendingDateRange.minUploadDate]);

  const handleDateChange = (event: CalendarProps<'single', Date | (Date | null)[]>) => {
    const dates = (event.value as (Date | null)[]) ?? [];
    setPendingDateRange({
      minUploadDate: formatDate(dates[0]),
      maxUploadDate: formatDate(dates[1]),
    });
  };

  useLayoutEffect(() => {
    if (!isOpen || !iconRef.current) return;

    const iconRect = iconRef.current.getBoundingClientRect();
    const popoverWidth = 360;
    const pagePadding = 24;
    const left = Math.min(iconRect.left, window.innerWidth - popoverWidth - pagePadding);

    setPopoverPosition({ left: Math.max(pagePadding, left), top: iconRect.bottom + window.scrollY });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        iconRef.current &&
        !iconRef.current.contains(target)
      ) {
        setFilterAnchor(null);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setFilterAnchor(null);
        iconRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, setFilterAnchor]);

  const openFilter = () => {
    if (isOpen) {
      setFilterAnchor(null);
      return;
    }

    setPendingNames(currentFilterValue);
    setPendingDateRange(currentDateRange);
    setSearchQuery('');
    setFilterAnchor(columnId);
  };

  const clearFilters = () => {
    setPendingNames([]);
    setPendingDateRange({});
    onApply([], {});
    setFilterAnchor(null);
  };

  return (
    <div className="relative pl-2">
      <button
        ref={iconRef}
        type="button"
        aria-label="Filter uploaded by"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        className="flex cursor-pointer items-center"
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          openFilter();
        }}
      >
        <FilterIcon
          className={classNames('h-4 w-4 text-[#D9D9D9]', {
            '[&>path]:fill-fletchers-green-800': isActive || isOpen,
          })}
        />
      </button>

      {isOpen &&
        popoverPosition &&
        createPortal(
          <div
            ref={popoverRef}
            role="dialog"
            aria-label="Filter uploaded by"
            className="flex w-[360px] flex-col overflow-hidden rounded-lg border bg-white"
            style={{
              position: 'absolute',
              top: popoverPosition.top,
              left: popoverPosition.left,
              zIndex: 9999,
              maxHeight: activeTab === 'date' ? '620px' : '440px',
              borderColor: '#E1E1E0',
              boxShadow: '4px 4px 25px rgba(0, 0, 0, 0.25)',
            }}
          >
            <div
              role="tablist"
              aria-label="Uploaded by filter type"
              className="border-fletchers-gray-110 grid grid-cols-2 border-b px-3 pt-3"
            >
              {(['name', 'date'] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab}
                  onClick={() => setActiveTab(tab)}
                  className={classNames(
                    'h-9 cursor-pointer border-b-2 text-[14px] font-semibold',
                    activeTab === tab
                      ? 'border-fletchers-green-800 text-fletchers-green-800'
                      : 'text-fletchers-gray-175 border-transparent',
                  )}
                >
                  By {tab}
                </button>
              ))}
            </div>

            {activeTab === 'name' ? (
              <>
                <div className="border-fletchers-gray-110 border-b p-3">
                  <div className="relative">
                    <i className="pi pi-search absolute top-1/2 left-[10px] -translate-y-1/2 text-[14px] text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search names"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      className="border-fletchers-gray-110 placeholder:text-fletchers-gray-200 h-8 w-full rounded border bg-white pr-8 pl-[35px] text-[14px] text-black focus:outline-none"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        aria-label="Clear name search"
                        onClick={() => setSearchQuery('')}
                        className="absolute top-1/2 right-[10px] -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600"
                      >
                        <CloseIcon width={10} height={10} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="min-h-[120px] flex-1 overflow-y-auto px-3 py-2">
                  {filteredOptions.length ? (
                    filteredOptions.map((option) => (
                      <label key={option} className="flex h-9 cursor-pointer items-center gap-4" title={option}>
                        <Checkbox
                          type="checkbox"
                          checked={pendingNames.includes(option)}
                          onChange={(event) =>
                            setPendingNames((names) =>
                              event.target.checked ? [...names, option] : names.filter((name) => name !== option),
                            )
                          }
                        />
                        <span className="overflow-hidden text-[14px] overflow-ellipsis whitespace-nowrap text-black">
                          {option}
                        </span>
                      </label>
                    ))
                  ) : (
                    <span className="text-fletchers-gray-175 text-[14px]">No results found</span>
                  )}
                </div>
              </>
            ) : (
              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 py-4">
                <div className="relative">
                  <ConfiguredCalendar
                    key={pendingDates ? 'has-dates' : 'no-dates'}
                    value={pendingDates}
                    selectionMode="range"
                    readOnlyInput={false}
                    hideOnRangeSelection
                    showIcon={false}
                    dateFormat="dd/mm/yy"
                    onChange={handleDateChange}
                    className="!h-[32px] w-full !text-[14px] [&_input]:!pr-8 [&_input]:!text-[14px] [&_input::placeholder]:!text-[14px]"
                  />
                  {pendingDates && (
                    <button
                      type="button"
                      aria-label="Clear date range"
                      className="absolute top-1/2 right-[10px] -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600 focus:outline-none"
                      onClick={() => setPendingDateRange({})}
                    >
                      <CloseIcon width={10} height={10} />
                    </button>
                  )}
                </div>
                <Calendar
                  value={pendingDates}
                  viewDate={pendingDates?.[1] || pendingDates?.[0] || new Date()}
                  selectionMode="range"
                  onChange={handleDateChange}
                  inline
                  showIcon={false}
                  dateFormat="dd/mm/yy"
                  className="w-full [&_.p-datepicker-calendar-container]:!px-0 [&_.p-datepicker-calendar-container]:!py-0 [&_.p-datepicker-header]:!px-0 [&_.p-datepicker-header]:!pt-0 [&_.p-datepicker-header]:!pb-4 [&_[data-pc-section]]:!px-0 [&_table]:!w-full [&_td]:!p-0 [&>div]:!w-full [&>div]:!border-0 [&>div]:!px-0 [&>div]:!py-0"
                  pt={{ dayLabel: { style: { height: '30px', width: '100%', margin: 0 } } }}
                />
              </div>
            )}

            <div className="border-fletchers-gray-110 flex items-center justify-end gap-2 border-t px-3 py-3">
              <Button
                outlined
                className="hover:!bg-fletchers-gray-50 !h-8 !px-4 !text-[14px] !font-semibold"
                onClick={clearFilters}
              >
                Clear
              </Button>
              <Button
                className="!h-8 !px-4 !text-[14px] !font-semibold"
                onClick={() => {
                  onApply(pendingNames, pendingDateRange);
                  setFilterAnchor(null);
                }}
              >
                Apply
              </Button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
